// ** React Imports
import { useCallback, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "helpers/toastWrapper";

// ** Redux & Store & Actions
import { connect, useDispatch, useSelector } from "react-redux";
import {
	modalReset,
	mergeBulkUserInfoAndMedia,
	getComments,
} from "store/modules/modal";

// ** Custom Global Components
import Block from "components/layout/Block";
import SwipersWrapperNew from "./components/Swipers/SwipersWrapperNew";
import UserWrapper from "./components/UserWrapper";
import CommentsSection from "./components/CommentsSection";

// ** Custom hooks
import useCanComment from "hooks/useCanComment";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// ** Styled Components
import { StyledModalMainSection } from "components/layout/Modal";

// #####################################################

const Modal = ({
	userInfo,
	contentInfo,
	list,
	comments,
	modalComponentRef,
	closeModal,
	showComments,
	setShowComments,
	userLogin,
	mediaType,
	contentId,
	albumId,
}) => {
	const dispatch = useDispatch();
	const history = useHistory();
	const { isPhone, isTablet, isDesktop } = useContext(deviceTypeContext);

	const userLimit = useSelector(
		(state) => state.userProfileMain.tolerances.limit
	);

	const { commentsDisabled, linkOnButtonToPayments, cantCommentMessage } =
		useCanComment({
			...contentInfo?.data,
			limit:
				typeof userLimit !== "undefined"
					? userLimit
					: contentInfo?.data?.limit,
		});
	const [visibleEditor, setVisibleEditor] = useState({
		type: null,
		id: null,
	});
	const [countComments, setCountComments] = useState(
		contentInfo?.data.countComment
	);
	const [iCommented, setICommented] = useState(contentInfo?.data.iComment);
	const [disabledAddComment, setDisabledAddComment] = useState(false);
	const [showCommentsSection, setShowCommentsSection] = useState(false);
	const [carouselSwiper, setCarouselSwiper] = useState(null);

	// ** Funkcja renderująca UserWrapper
	const renderUserWrapper = useCallback(() => {
		return userInfo?.data?.login && (isPhone || isTablet) ? (
			<Block flex pt={1} pb={3}>
				<UserWrapper
					userInfo={userInfo}
					contentInfo={contentInfo}
					showComments={isPhone || isTablet ? true : showComments}
					closeModal={closeModal}
				/>
			</Block>
		) : null;
	}, [
		userInfo?.data?.login,
		showComments,
		contentInfo?.data?.contentId,
		isPhone,
		isTablet,
	]);

	const onCommentClick = () => {
		if (
			visibleEditor.type === "ADD_COMMENT" &&
			visibleEditor.id === "modal"
		) {
			setVisibleEditor({
				type: null,
				id: null,
			});

			if (isPhone || isTablet) {
				setShowCommentsSection(false);
			}
		} else {
			setVisibleEditor({
				type: "ADD_COMMENT",
				id: "modal",
			});

			if ((isPhone || isTablet) && !showCommentsSection) {
				setShowCommentsSection(true);
				carouselSwiper.zoom.out();

				if (!comments?.comments?.length && countComments) {
					dispatch(
						getComments({
							userId: contentInfo.data.userId,
							elementId: contentInfo.data.contentId,
							limit: 10,
							type: mediaType,
							more: true,
						})
					).then(() => {
						setTimeout(() => {
							if (modalComponentRef.current) {
								modalComponentRef.current.scrollTop =
									carouselSwiper.height + 57;
							}
						}, 300);
					});
				} else {
					setTimeout(() => {
						if (modalComponentRef.current) {
							modalComponentRef.current.scrollTop =
								carouselSwiper.height + 57;
						}
					}, 300);
				}
			}
		}
	};

	// ** Efekt na podstawie loginu użytkownika pobiera dane potrzebne do wyświetlenia modala
	// ** Po odmontowaniu komponentu resetuje stan modala
	useEffect(() => {
		const controller = new AbortController();
		if (userLogin) {
			//console.log("MODAL useEffect - if (userLogin):", userLogin);
			dispatch(
				mergeBulkUserInfoAndMedia({
					userLogin,
					elementId: contentId,
					type: mediaType,
					vector: "both",
					album: albumId,
					limit: isPhone ? 10 : isTablet ? 20 : 30,
				})
			);
		}
		return () => {
			if (userLogin) {
				//console.log("MODAL useEffect - return:", userLogin);
				controller.abort();
				dispatch(modalReset());
			}
		};
	}, [userLogin]);

	// ** Przekierowanie na stronę 404 gdy nie znaleziono użytkownika lub zdjęcia.
	useEffect(() => {
		if (userInfo.error || list.error) {
			if (userInfo.errorText) {
				toast.error(userInfo.errorText);
			}
			history.replace("/error404");
		}
	}, [userInfo.error, list.error]);

	useEffect(() => {
		setCountComments(contentInfo?.data.countComment);
	}, [contentInfo?.data.countComment, contentInfo.data.contentId]);

	useEffect(() => {
		setICommented(contentInfo?.data.iComment);
	}, [contentInfo?.data.iComment, contentInfo.data.contentId]);

	useEffect(() => {
		if (visibleEditor.type || visibleEditor.id) {
			setVisibleEditor({
				type: null,
				id: null,
			});
		}
	}, [contentId]);

	// #####################################################

	return (
		<>
			<StyledModalMainSection>
				<Block relative flex column fullHeight>
					<Block pr={isPhone ? 2 : 0} pl={isPhone ? 4 : 0}>
						{/* Funkcja renderujaca Avatar i Login użytkownika na Mobile */}
						{renderUserWrapper()}
					</Block>
					<SwipersWrapperNew
						carouselSwiper={carouselSwiper}
						setCarouselSwiper={setCarouselSwiper}
						mediaType={mediaType}
						mediaData={list?.data}
						mediaMeta={list?.meta}
						setShowComments={setShowComments}
						contentInfo={contentInfo}
						iCommented={iCommented}
						disabledAddComment={disabledAddComment}
						countComments={countComments}
						onCommentClick={onCommentClick}
						contentIdUrl={contentId}
						showCommentsSection={showCommentsSection}
						setShowCommentsSection={setShowCommentsSection}
						setVisibleEditor={setVisibleEditor}
					/>
				</Block>
			</StyledModalMainSection>
			{(showCommentsSection || isDesktop) && (
				<CommentsSection
					contentIdUrl={contentId}
					mediaType={mediaType}
					albumId={albumId}
					showComments={showComments}
					closeModal={closeModal}
					commentsDisabled={commentsDisabled}
					cantCommentMessage={cantCommentMessage}
					linkOnButtonToPayments={linkOnButtonToPayments}
					onCommentClick={onCommentClick}
					visibleEditor={visibleEditor}
					setVisibleEditor={setVisibleEditor}
					countComments={countComments}
					setCountComments={setCountComments}
					iCommented={iCommented}
					setICommented={setICommented}
					disabledAddComment={disabledAddComment}
					setDisabledAddComment={setDisabledAddComment}
				/>
			)}
		</>
	);
};

// #####################################################

const mapStateToProps = ({
	modal: { userInfo, contentInfo, list, comments },
}) => ({
	userInfo,
	contentInfo,
	list,
	comments,
});

export default connect(mapStateToProps, {})(Modal);

// #####################################################
